import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, computed, inject, OnInit } from '@angular/core';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { extractXmlProps, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import { BarcodeFormat } from '@zxing/library';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import {
  BarcodeScannerDialogComponent
} from '../../../shared/dialogs/barcode-scanner-dialog/barcode-scanner-dialog.component';
import { INTERACTIVE_DIALOG_SIZE_STYLES } from '../../../../utils/constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

enum BarcodeMode {
  Show = '0',
  Compare = '1'
}

interface BarcodeItemProperties {
  text: string;
  resultValue: string;
  resultUser: string;
  resultTime: string;
  style: string;
  barcodeMode: string;
  barcodeMatches: string;
  expectedBarcode: string;
  detectedBarcode: string;
}

@Component({
  selector: 'lfe-barcode-item-form',
  imports: [FormsModule, ReactiveFormsModule, NgClass],
  templateUrl: './barcode-item-form.component.html',
  standalone: true,
  styleUrl: './barcode-item-form.component.css'
})
export class BarcodeItemFormComponent extends InteractiveItemComponent<BarcodeItemProperties> implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;

  allowedFormats = [
    BarcodeFormat.QR_CODE,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.ITF,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.PDF_417,
    BarcodeFormat.AZTEC
  ];

  initialValue = '';

  properties: InteractiveItemProperties & BarcodeItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: 'Normal',
    barcodeMode: BarcodeMode.Show,
    barcodeMatches: 'False',
    expectedBarcode: '',
    detectedBarcode: ''
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlId = this.properties.customId || this.properties.id;
    this.initialValue = this.properties.resultValue || '';
    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & BarcodeItemProperties {
    const props = extractXmlProps(xml);

    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id: props['ID'],
      customId: props['CustomID'],
      activationRule: props['ActivationRule'] || '',
      resultValue: props['ResultValue'] || '',
      resultUser: props['ResultUser'] || '',
      resultTime: props['ResultTime'] || '',
      text: props['Text'] || '',
      style: props['Style'] || 'Normal',
      barcodeMode: props['BarcodeMode'] || BarcodeMode.Show,
      barcodeMatches: props['BarcodeMatches'] || 'False',
      expectedBarcode: props['ExpectedBarcode'] || '',
      detectedBarcode: props['DetectedBarcode'] || '',
      isGenericRepeatItem
    };
  }

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `barcode_${this.currentId()}`);

  isCompareMode = computed(() => this.properties.barcodeMode === BarcodeMode.Compare);
  isShowMode = computed(() => this.properties.barcodeMode === BarcodeMode.Show);

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.properties.resultValue !== this.initialValue) {
      this.setResultChildren(element);
      upsertChildren(element, {
        DetectedBarcode: this.properties.detectedBarcode
      });
    }

    if (this.isCompareMode()) {
      upsertChildren(element, {
        BarcodeMatches: this.properties.barcodeMatches
      });
    }

    return xmlElementToString(element) || '';
  }

  onScanClick(e: Event): void {
    if (this.properties.resultValue) {
      this.clearBarcode(e);
    }

    e.preventDefault();

    const dialogRef = this.dialog.open(BarcodeScannerDialogComponent, {
      ...INTERACTIVE_DIALOG_SIZE_STYLES,
      disableClose: false,
      data: {
        title: this.translateService.instant('liveforms-editor.barcode-item.scanner.title'),
        allowedFormats: this.allowedFormats
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((barcode: string | undefined) => {
        if (barcode) {
          this.processBarcode(barcode);
          this.cdr.markForCheck();
        }
      });
  }

  private processBarcode(barcode: string): void {
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();
    this.properties.detectedBarcode = barcode;

    if (this.isCompareMode()) {
      const matches = barcode === this.properties.expectedBarcode;
      this.properties.barcodeMatches = matches ? 'True' : 'False';

      this.properties.resultValue = matches
        ? this.translateService.instant('liveforms-editor.barcode-item.result.expected-match', {
            barcode: this.properties.expectedBarcode
          })
        : this.translateService.instant('liveforms-editor.barcode-item.result.expected-no-match', {
            barcode: this.properties.expectedBarcode
          });
    } else {
      this.properties.resultValue = this.translateService.instant(
        'liveforms-editor.barcode-item.result.scanned-barcode',
        { barcode }
      );
    }
    this.formControl?.setValue(barcode);
    this.formControl?.markAsDirty();
  }

  clearBarcode(e: Event): void {
    e.preventDefault();
    this.properties.resultValue = '';
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();

    if (this.isCompareMode()) {
      this.properties.barcodeMatches = 'False';
    }

    this.formControl?.setValue('');
  }
}
